import { Alert, Divider, Drawer } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import AddressMapViewer from '@src/components/AddressMapViewer';
import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { AUDIT_DATE_FORMAT, DateUtil } from '@src/packages/utils/dates';
import { isUsablePersonDisplayName } from '@src/packages/utils/user';
import IntlMessages from '@src/util/IntlMessages';

import type {
  MatrixAttendanceDrawerData,
  MatrixAttendanceDrawerTarget,
} from '../../hooks/useWorkJourneyMatrixAttendanceDrawer';
import {
  buildAttendanceLocationMarkers,
  getAttendanceMarkLabelId,
} from '../../utils/buildAttendanceLocationMarkers';
import {
  buildAttendanceMarkingRows,
  formatAttendanceSectionDate,
} from '../../utils/buildAttendanceMarkingRows';
import {
  canEditAttendanceMarking,
  getAttendanceEditBlockReason,
  getAttendanceEditUnlockMs,
  type AttendanceEditBlockReason,
} from '../../utils/canEditAttendanceMarking';
import { isReplacementCell } from '../../utils/isReplacementCell';
import { resolveAttendanceObservationFromRecord } from '../../utils/resolveAttendanceObservation';
import styles from './styles.module.less';

type WorkJourneyMatrixAttendanceDrawerProps = {
  open: boolean;
  loading: boolean;
  error: string | null;
  target: MatrixAttendanceDrawerTarget | null;
  data: MatrixAttendanceDrawerData | null;
  onClose: () => void;
  onEdit?: (data: MatrixAttendanceDrawerData) => void;
};

const OBSERVATION_TONE_CLASS = {
  auj: styles.observationAuj,
  aui: styles.observationAui,
  vac: styles.observationVac,
  lm: styles.observationLm,
  pgr: styles.observationPgr,
  psg: styles.observationPsg,
  pren: styles.observationPren,
  postn: styles.observationPostn,
  rem: styles.observationRem,
  abs: styles.observationAbs,
} as const;

export const WorkJourneyMatrixAttendanceDrawer = ({
  open,
  loading,
  error,
  target,
  data,
  onClose,
  onEdit,
}: WorkJourneyMatrixAttendanceDrawerProps) => {
  const intl = useIntl();

  const title = useMemo(() => {
    const name =
      (isUsablePersonDisplayName(data?.user.displayName) ? data?.user.displayName.trim() : '') ||
      (isUsablePersonDisplayName(target?.user.displayName) ? target?.user.displayName.trim() : '') ||
      '';
    if (!target?.date) {
      return intl.formatMessage(
        { id: 'workJourney.management.matrix.attendance.titleFallback' },
        { name: name || '—' },
      );
    }
    const ms =
      DateUtil.getTimestampMs(`${target.date}T12:00:00`) ?? Date.parse(`${target.date}T12:00:00`);
    const weekday = Number.isFinite(ms) ? intl.formatDate(ms, { weekday: 'long' }) : '';
    const dayMonth = Number.isFinite(ms) ? DateUtil.toDateString(ms, 'DD/MM') : target.date;
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.attendance.title' },
      { name: name || '—', weekday, date: dayMonth },
    );
  }, [data?.user.displayName, intl, target?.date, target?.user.displayName]);

  const observation = useMemo(
    () => resolveAttendanceObservationFromRecord(intl, data?.record, target?.cell),
    [data?.record, intl, target?.cell],
  );

  const isAbsentObservation = !!observation && observation.tone !== 'rem';

  // A coverage day exists as an exception before the attendance record does,
  // so the missing record is expected rather than a lookup failure.
  const errorMessageId = useMemo(() => {
    if (error !== 'not_found') return 'workJourney.management.matrix.attendance.loadError';
    return isReplacementCell(target?.cell)
      ? 'workJourney.management.matrix.attendance.coverageWithoutRecord'
      : 'workJourney.management.matrix.attendance.notFound';
  }, [error, target?.cell]);

  const sectionDate = target?.date ? formatAttendanceSectionDate(intl, target.date) : '';
  const markingRows = useMemo(
    () =>
      buildAttendanceMarkingRows(intl, data?.record ?? null, {
        isAbsent: isAbsentObservation,
        absentDate: target?.date,
      }),
    [data?.record, intl, isAbsentObservation, target?.date],
  );

  const contactParts = [data?.user.phone, data?.user.email].filter(Boolean);
  const addressLabel = data?.address.label;

  const locationMarkers = useMemo(() => {
    if (!data) return [];
    return buildAttendanceLocationMarkers({
      workplace: data.address.mapAddress,
      workplaceLabel:
        addressLabel ||
        intl.formatMessage({
          id: 'workJourney.management.matrix.attendance.map.workplace',
        }),
      events: data.events,
      formatMarkLabel: attendanceType => {
        const labelId = getAttendanceMarkLabelId(attendanceType);
        return labelId
          ? intl.formatMessage({ id: labelId })
          : intl.formatMessage({
              id: 'workJourney.management.matrix.attendance.map.markFallback',
            });
      },
    });
  }, [addressLabel, data, intl]);

  const showLocationSection = Boolean(addressLabel) || locationMarkers.length > 0;
  const hasWorkplaceMarker = locationMarkers.some(marker => marker.variant === 'workplace');
  const hasMarkMarkers = locationMarkers.some(marker => marker.variant === 'mark');

  const editOptions = useMemo(
    () => ({ scheduledDate: target?.date }),
    [target?.date],
  );

  // Refresh while the drawer stays open so the Alert / Edit CTA flip when the
  // unlock instant passes (memos must not freeze a single Date.now() forever).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!open || !data || loading) return undefined;
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(intervalId);
  }, [open, data, loading, target?.date, target?.userId]);

  const editBlockReason: AttendanceEditBlockReason | null = useMemo(() => {
    if (!data || loading) return null;
    return getAttendanceEditBlockReason(data.record ?? null, nowMs, editOptions);
  }, [data, editOptions, loading, nowMs]);

  const editUnlockMs = useMemo(() => {
    if (!data || loading || !editBlockReason) return null;
    return getAttendanceEditUnlockMs(data.record ?? null, nowMs, editOptions);
  }, [data, editBlockReason, editOptions, loading, nowMs]);

  const canEdit = canEditAttendanceMarking(data?.record ?? null, nowMs, editOptions);

  const editBlockMessageId =
    editBlockReason === 'journey_not_finished'
      ? 'workJourney.management.matrix.attendance.edit.disabled.journeyNotFinished'
      : editBlockReason === 'cooldown'
        ? 'workJourney.management.matrix.attendance.edit.disabled.cooldown'
        : null;

  const handleEdit = useCallback(() => {
    if (!data || !canEdit) return;
    onEdit?.(data);
  }, [canEdit, data, onEdit]);

  const actionButtons = useMemo((): FilterContainerActionButton[] => {
    const editDisabled = !data || loading || !canEdit;
    const tooltipText = editBlockMessageId
      ? intl.formatMessage({ id: editBlockMessageId })
      : undefined;

    return [
      {
        label: <IntlMessages id="button.edit" />,
        action: handleEdit,
        disabled: editDisabled,
        type: editDisabled ? 'default' : 'primary',
        // FilterContainer wraps disabled buttons in <span> so Ant Tooltip still shows.
        tooltipText,
      },
    ];
  }, [canEdit, data, editBlockMessageId, handleEdit, intl, loading]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      className={styles.drawer}
    >
      <BoxContainer loading={loading}>
        <BoxContainer fullContent fixed bottomBorder>
          <FilterContainer
            title={<Title.Header value={title} />}
            goBack={onClose}
            actionButtons={actionButtons}
          />
        </BoxContainer>

        <BoxContainer fullContent>
          {error && (
            <p className={styles.errorText}>
              <IntlMessages id={errorMessageId} />
            </p>
          )}

          {observation && (
            <Alert
              className={classNames(
                styles.observationAlert,
                OBSERVATION_TONE_CLASS[observation.tone],
              )}
              message={observation.label}
              type="error"
              showIcon={false}
            />
          )}

          {editBlockMessageId && (
            <Alert
              className={styles.editLockAlert}
              type="info"
              showIcon
              message={intl.formatMessage({ id: editBlockMessageId })}
              description={
                editUnlockMs != null
                  ? intl.formatMessage(
                      {
                        id: 'workJourney.management.matrix.attendance.edit.unlockAt',
                      },
                      {
                        when: DateUtil.toDateString(editUnlockMs, AUDIT_DATE_FORMAT),
                      },
                    )
                  : undefined
              }
            />
          )}

          {data && (
            <>
              <div className={styles.userHeader}>
                <MemberAvatar
                  size={40}
                  user={{
                    displayName: data.user.displayName,
                    photoURL: data.user.photoURL ?? undefined,
                  }}
                />

                <div className={styles.userMeta}>
                  <div className={styles.userName}>{data.user.displayName}</div>
                  {contactParts.length > 0 && (
                    <div className={styles.userContact}>{contactParts.join(' | ')}</div>
                  )}
                </div>
              </div>
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Title.SubTitle
                    value={
                      <IntlMessages id="workJourney.management.matrix.attendance.markingSection" />
                    }
                  />
                  {sectionDate && <span className={styles.sectionDate}>{sectionDate}</span>}
                </div>

                <dl className={styles.detailList}>
                  {markingRows.map((row, index) => (
                    <div key={row.key}>
                      {index > 0 && <Divider className={styles.rowDivider} />}
                      <div className={styles.detailRow}>
                        <dt>
                          <IntlMessages id={row.labelId} />
                        </dt>
                        <dd>{row.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </section>
              {showLocationSection && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <Title.SubTitle
                      value={
                        <IntlMessages id="workJourney.management.matrix.attendance.locationSection" />
                      }
                    />
                    {addressLabel && <span className={styles.addressLabel}>{addressLabel}</span>}
                  </div>

                  {locationMarkers.length > 0 && (
                    <>
                      <div className={styles.mapWrap}>
                        <AddressMapViewer
                          markers={locationMarkers}
                          visible={open}
                          customHeight="280px"
                        />
                      </div>
                      {(hasWorkplaceMarker || hasMarkMarkers) && (
                        <ul className={styles.mapLegend}>
                          {hasWorkplaceMarker && (
                            <li>
                              <span
                                className={classNames(
                                  styles.legendIcon,
                                  styles.legendIconWorkplace,
                                )}
                                aria-hidden
                              />
                              <IntlMessages id="workJourney.management.matrix.attendance.map.legendWorkplace" />
                            </li>
                          )}
                          {hasMarkMarkers && (
                            <li>
                              <span
                                className={classNames(styles.legendIcon, styles.legendIconMark)}
                                aria-hidden
                              />
                              <IntlMessages id="workJourney.management.matrix.attendance.map.legendMarks" />
                            </li>
                          )}
                        </ul>
                      )}
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default WorkJourneyMatrixAttendanceDrawer;
